export const RateSheetDimension = {
  Species: 'species',
  Length: 'length',
  Diameter: 'diameter',
  Grade: 'grade',
  EndUseSort: 'endUseSort'
}

const ALL_DIMENSIONS = Object.values(RateSheetDimension)
const OUTSIDE_VALUE = -1

const FOUR_DIMENSION_SHAPES = [
  [RateSheetDimension.Species, RateSheetDimension.Grade, RateSheetDimension.EndUseSort, RateSheetDimension.Diameter],
  [RateSheetDimension.Species, RateSheetDimension.Grade, RateSheetDimension.EndUseSort, RateSheetDimension.Length],
  [RateSheetDimension.Species, RateSheetDimension.Grade, RateSheetDimension.Diameter, RateSheetDimension.Length],
  [RateSheetDimension.Species, RateSheetDimension.EndUseSort, RateSheetDimension.Diameter, RateSheetDimension.Length],
  [RateSheetDimension.Grade, RateSheetDimension.EndUseSort, RateSheetDimension.Diameter, RateSheetDimension.Length]
]

function toId (value) {
  return value === null || value === undefined ? null : Number(value)
}

export function lineDimensionIds (line) {
  return {
    [RateSheetDimension.Species]: toId(line.speciesGroupId ?? line.speciesGroup?.speciesGroupId),
    [RateSheetDimension.Length]: toId(line.lengthGroupId ?? line.lengthGroup?.lengthGroupId),
    [RateSheetDimension.Diameter]: toId(line.diameterGroupId ?? line.diameterGroup?.diameterGroupId),
    [RateSheetDimension.Grade]: toId(line.gradeId ?? line.grade?.gradeId),
    [RateSheetDimension.EndUseSort]: toId(line.endUseSortId ?? line.endUseSort?.endUseSortId)
  }
}

export function specifiedDimensions (line) {
  const ids = lineDimensionIds(line)
  return ALL_DIMENSIONS.filter(dimension => ids[dimension] !== null)
}

export function buildScalingLookup ({ speciesGroups = [], lengthGroups = [], diameterGroups = [] }) {
  return {
    speciesByGroupId: new Map(speciesGroups.map(group => [
      Number(group.speciesGroupId),
      new Set((group.species ?? []).map(species => Number(species.speciesId)))
    ])),
    lengthsByGroupId: new Map(lengthGroups.map(group => [
      Number(group.lengthGroupId),
      new Set((group.logLengths ?? []).map(logLength => Number(logLength.length)))
    ])),
    rangeByDiameterGroupId: new Map(diameterGroups.map(group => [
      Number(group.diameterGroupId),
      { lower: group.lowerBound, upper: group.upperBound }
    ]))
  }
}

function dimensionMatches (dimension, id, log, lookup) {
  if (id === null) return false
  switch (dimension) {
    case RateSheetDimension.Species:
      return lookup.speciesByGroupId.get(id)?.has(log.speciesId) ?? false
    case RateSheetDimension.Length:
      return lookup.lengthsByGroupId.get(id)?.has(log.length) ?? false
    case RateSheetDimension.Diameter: {
      const range = lookup.rangeByDiameterGroupId.get(id)
      return !!range && range.lower <= log.diameter && range.upper >= log.diameter
    }
    case RateSheetDimension.Grade:
      return id === log.gradeId
    case RateSheetDimension.EndUseSort:
      return id === (log.endUseSortId ?? null)
  }
  return false
}

function matchesAll (ids, dimensions, log, lookup) {
  return dimensions.every(dimension => dimensionMatches(dimension, ids[dimension], log, lookup))
}

function matchesTierOne (ids, log, lookup) {
  return dimensionMatches(RateSheetDimension.Species, ids.species, log, lookup) &&
    ids.grade === log.gradeId &&
    ids.endUseSort === (log.endUseSortId ?? null) &&
    dimensionMatches(RateSheetDimension.Diameter, ids.diameter, log, lookup) &&
    dimensionMatches(RateSheetDimension.Length, ids.length, log, lookup)
}

export function lineMatcher (lines, lookup) {
  const entries = lines.map(line => {
    const ids = lineDimensionIds(line)
    return { line, ids, dimensions: ALL_DIMENSIONS.filter(dimension => ids[dimension] !== null) }
  })
  const fourDimensionEntries = entries.filter(entry => entry.dimensions.length === 4)
  const entriesByDescendingCount = [3, 2, 1].map(count => entries.filter(entry => entry.dimensions.length === count))

  return log => {
    const tierOne = entries.find(entry => matchesTierOne(entry.ids, log, lookup))
    if (tierOne) return tierOne.line

    for (const shape of FOUR_DIMENSION_SHAPES) {
      const match = fourDimensionEntries.find(entry => matchesAll(entry.ids, shape, log, lookup))
      if (match) return match.line
    }

    for (const countEntries of entriesByDescendingCount) {
      const match = countEntries.find(entry => matchesAll(entry.ids, entry.dimensions, log, lookup))
      if (match) return match.line
    }

    return null
  }
}

export function lineDimensionResults (line, log, lookup) {
  const ids = lineDimensionIds(line)
  return specifiedDimensions(line).map(dimension => ({
    dimension,
    matches: dimensionMatches(dimension, ids[dimension], log, lookup)
  }))
}

export function coverageOf (candidate, sheetLines, combinations, lookup) {
  const matchWithCandidate = lineMatcher([...sheetLines, candidate], lookup)
  return combinations.reduce((coverage, combination) => {
    const covered = matchWithCandidate(combination) === candidate
    return {
      coveredLogs: coverage.coveredLogs + (covered ? combination.logCount : 0),
      totalLogs: coverage.totalLogs + combination.logCount
    }
  }, { coveredLogs: 0, totalLogs: 0 })
}

function membershipRepresentatives (values, groupsById, isMember) {
  const representativesBySignature = new Map()
  values.forEach(value => {
    const signature = [...groupsById.entries()]
      .filter(([, group]) => isMember(group, value))
      .map(([groupId]) => groupId)
      .join(',')
    if (!representativesBySignature.has(signature)) representativesBySignature.set(signature, value)
  })
  return [...representativesBySignature.values()]
}

function sampleSpace (lines, lookup) {
  const allSpecies = new Set([...lookup.speciesByGroupId.values()].flatMap(species => [...species]))
  const allLengths = new Set([...lookup.lengthsByGroupId.values()].flatMap(lengths => [...lengths]))
  const diameterBreakpoints = new Set([OUTSIDE_VALUE])
  lookup.rangeByDiameterGroupId.forEach(range => {
    diameterBreakpoints.add(range.lower)
    diameterBreakpoints.add(range.upper + 1)
  })
  const referenced = dimension => lines.map(line => lineDimensionIds(line)[dimension]).filter(id => id !== null)
  const isInRange = (range, value) => range.lower <= value && range.upper >= value

  return {
    [RateSheetDimension.Species]: membershipRepresentatives([...allSpecies, OUTSIDE_VALUE], lookup.speciesByGroupId, (group, value) => group.has(value)),
    [RateSheetDimension.Length]: membershipRepresentatives([...allLengths, OUTSIDE_VALUE], lookup.lengthsByGroupId, (group, value) => group.has(value)),
    [RateSheetDimension.Diameter]: membershipRepresentatives([...diameterBreakpoints], lookup.rangeByDiameterGroupId, isInRange),
    [RateSheetDimension.Grade]: [...new Set(referenced(RateSheetDimension.Grade)), OUTSIDE_VALUE],
    [RateSheetDimension.EndUseSort]: [...new Set(referenced(RateSheetDimension.EndUseSort)), null, OUTSIDE_VALUE]
  }
}

function withinLine (dimension, id, value, lookup) {
  if (id === null) return true
  if (dimension === RateSheetDimension.Grade || dimension === RateSheetDimension.EndUseSort) return id === value
  return dimensionMatches(dimension, id, { speciesId: value, length: value, diameter: value }, lookup)
}

function someSharedLog (firstIds, secondIds, space, lookup, predicate) {
  const [species, lengths, diameters, grades, endUseSorts] = ALL_DIMENSIONS.map(dimension => space[dimension]
    .filter(value => withinLine(dimension, firstIds[dimension], value, lookup) && withinLine(dimension, secondIds[dimension], value, lookup)))

  for (const speciesId of species) {
    for (const length of lengths) {
      for (const diameter of diameters) {
        for (const gradeId of grades) {
          for (const endUseSortId of endUseSorts) {
            if (predicate({ speciesId, length, diameter, gradeId, endUseSortId })) return true
          }
        }
      }
    }
  }
  return false
}

export function linesTakenOverBy (candidate, sheetLines, lookup) {
  const linesWithCandidate = [...sheetLines, candidate]
  const matchWithoutCandidate = lineMatcher(sheetLines, lookup)
  const matchWithCandidate = lineMatcher(linesWithCandidate, lookup)
  const space = sampleSpace(linesWithCandidate, lookup)
  const candidateIds = lineDimensionIds(candidate)

  return sheetLines.filter(line => someSharedLog(candidateIds, lineDimensionIds(line), space, lookup,
    log => matchWithCandidate(log) === candidate && matchWithoutCandidate(log) === line))
}

export function sameDimensions (first, second) {
  const firstIds = lineDimensionIds(first)
  const secondIds = lineDimensionIds(second)
  return ALL_DIMENSIONS.every(dimension => firstIds[dimension] === secondIds[dimension])
}
